import { type DateSelectArg, type EventClickArg, type EventInput, type EventSourceInput } from '@fullcalendar/core';
import icalendarPlugin from '@fullcalendar/icalendar';
import interactionPlugin from '@fullcalendar/interaction';
import luxon2Plugin, { toLuxonDateTime } from '@fullcalendar/luxon3';
import FullCalendar from '@fullcalendar/react';
import timegridPlugin from '@fullcalendar/timegrid';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, Button, IconButton, Stack, Tooltip, useTheme } from '@mui/material';
import { type MutationOptions } from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import { DateTime } from 'luxon';
import React, { type FC, useRef, useState } from 'react';
import { useEffectOnce } from 'usehooks-ts';

import {
  useCreateMonitoringHourRule,
  useDeleteMonitoringHourEvent,
  useDeleteMonitoringHourRule,
  useMonitoringHoursEvents,
  useMonitoringHoursRules,
  useUpdateMonitoringHourEvent,
  useUpdateMonitoringHourRule,
  usePatient,
} from '../api/index.js';
import { useCheckPermissions } from '../auth/index.js';
import { businessHours, Loading, NUMBER_OF_DAYS_BEHIND } from '../components/index.js';
import { type MonitoringHoursDialogMode, MonitoringHoursForm } from '../components/MonitoringHours/index.js';
import MonitoringHoursRulesList from '../components/MonitoringHours/MonitoringHoursRulesList.js';
import { ruleNextOccurrence } from '../components/MonitoringHours/ruleSummary.js';
import useSnackBar from '../hooks/use-snack-bar.js';
import { type MonitoringHoursEvent, type MonitoringHoursRule, MonitoringHoursType } from '../models/index.js';
import { type MonitoringHoursRuleForm, UpdateType } from '../types/index.js';
import {
  Events,
  track,
  dateTimeToLocalDateSameTime,
  dateToUTCString,
  localDateToSpecificTimeZoneDate,
  maximumDateInUTCString,
} from '../utils/index.js';


const PLUGINS = [luxon2Plugin, interactionPlugin, timegridPlugin, icalendarPlugin];

interface MonitoringHoursProps {
  patientId?: string;
}

const MonitoringHours: FC<MonitoringHoursProps> = ({ patientId }) => {
  const [open, setOpen] = useState(false);
  const today = DateTime.now();
  const [selectedRule, setSelectedRule] = useState<MonitoringHoursRuleForm>();
  const [dialogMode, setDialogMode] = useState<MonitoringHoursDialogMode>('create');
  const [events, setEvents] = useState<EventSourceInput>([]);
  const defaultCalendarDate = {
    start: today.startOf('week', { useLocaleWeeks: true }),
    end: today.endOf('week', { useLocaleWeeks: true }),
  };
  const [calendarDates, setCalendarDates] = useState<{ start: DateTime; end: DateTime }>(defaultCalendarDate);
  const [isTimeZoneSet, setTimezoneSet] = useState(false);
  const snackBar = useSnackBar();

  const calendarRef = useRef<FullCalendar>(null!);
  const theme = useTheme();
  const { canCreateMonitoringHours, canUpdateMonitoringHours } = useCheckPermissions();

  const patient = usePatient(
    patientId,
    {
      includeCameras: true,
      includeEmergencyContacts: true,
    },
    {
      onSuccess: (data) => {
        setCalendarDates({
          start: today.setZone(data.timeZone).startOf('week', { useLocaleWeeks: true }),
          end: today.setZone(data.timeZone).endOf('week', { useLocaleWeeks: true }),
        });
        setTimezoneSet(true);
      },
    },
  );
  const monitoringHoursEvents = useMonitoringHoursEvents(
    patientId,
    calendarDates.start.toUTC(),
    calendarDates.end.toUTC(),
    {
      enabled: patient.isSuccess && isTimeZoneSet,
      onSettled: (data) => {
        if (data && patient.data) {
          const _events = data.map((monitoringHoursEvent: MonitoringHoursEvent): EventInput => {
            const { start, end, ruleId, id } = monitoringHoursEvent;

            return {
              allDay: false,
              title: monitoringHoursEvent.rule?.type === MonitoringHoursType.MONITORING ? 'Monitoring' : 'Check-in',
              start: DateTime.fromISO(start).setZone(patient.data.timeZone).toJSDate(),
              end: DateTime.fromISO(end).setZone(patient.data.timeZone).toJSDate(),

              // Custom props
              extendedProps: {
                ruleId,
                eventId: id,
              },
            };
          });

          setEvents(_events);
        }
      },
    },
  );

  const monitoringHourRules = useMonitoringHoursRules(patientId, {
    enabled: patient.isSuccess,
  });

  const createMonitoringHourRule = useCreateMonitoringHourRule();
  const updateMonitoringHourRule = useUpdateMonitoringHourRule();
  const deleteMonitoringHourRule = useDeleteMonitoringHourRule();

  const updateMonitoringHourEvent = useUpdateMonitoringHourEvent();
  const deleteMonitoringHourEvent = useDeleteMonitoringHourEvent();

  useEffectOnce(() => {
    track(Events.MonitoringHoursView, {
      patientId,
    });
  });

  const onDateChange = (event: 'prevWeek' | 'nextWeek') => {
    setCalendarDates((prevDates) => {
      return {
        start: prevDates.start.plus({ week: event === 'nextWeek' ? 1 : -1 }),
        end: prevDates.end.plus({ week: event === 'nextWeek' ? 1 : -1 }),
      };
    });
    const calendarApi = calendarRef.current.getApi();
    if (event === 'nextWeek') {
      calendarApi.incrementDate({ days: 7 });
    } else {
      calendarApi.incrementDate({ days: -7 });
    }
  };

  const onDateSelect = (event: DateSelectArg) => {
    if (!canCreateMonitoringHours) {
      return;
    }
    const start = toLuxonDateTime(event.start, event.view.calendar);
    const end = toLuxonDateTime(event.end, event.view.calendar);
    setDialogMode('create');
    setSelectedRule({
      start: dateTimeToLocalDateSameTime(start),
      end: dateTimeToLocalDateSameTime(end),
      type: MonitoringHoursType.MONITORING,
      updateType: UpdateType.EVENT,
      repeats: false,
    });
    setOpen(true);
  };

  const onAddRule = () => {
    if (!canCreateMonitoringHours) {
      return;
    }
    const patientTimeZone = patient.data?.timeZone;
    const start = DateTime.now().setZone(patientTimeZone || 'local').plus({ hour: 1 }).startOf('hour');
    setDialogMode('create');
    setSelectedRule({
      start: dateTimeToLocalDateSameTime(start),
      end: dateTimeToLocalDateSameTime(start.plus({ hour: 1 })),
      type: MonitoringHoursType.MONITORING,
      updateType: UpdateType.EVENT,
      repeats: false,
    });
    setOpen(true);
  };

  const onEditRule = (rule: MonitoringHoursRule) => {
    if (!canUpdateMonitoringHours || !patient.data) {
      return;
    }
    const nextOccurrence = ruleNextOccurrence(rule, patient.data.timeZone);
    if (!nextOccurrence || rule.durationMinutes === undefined) {
      snackBar.error('This rule is missing its schedule and cannot be edited.');
      return;
    }
    setDialogMode('series');
    setSelectedRule({
      id: rule.id,
      ruleStartDate: rule.startDate,
      start: dateTimeToLocalDateSameTime(nextOccurrence),
      end: dateTimeToLocalDateSameTime(nextOccurrence.plus({ minutes: rule.durationMinutes })),
      repeats: rule.repeats,
      freq: rule.freq,
      interval: rule.interval,
      byDay: rule.byDay,
      until: rule.until ? DateTime.fromISO(rule.until).toJSDate() : undefined,
      type: rule.type,
      checkInDuration: rule.checkInDuration,
      checkInInterval: rule.checkInInterval,
      updateType: UpdateType.RULE,
    });
    setOpen(true);
  };

  const onEventClick = (event: EventClickArg) => {
    if (!canUpdateMonitoringHours) {
      return;
    }

    const ruleId = event.event.extendedProps.ruleId;
    const eventId = event.event.extendedProps.eventId;
    const { start, end } = event.event;
    if (ruleId && monitoringHourRules.data && patient.data) {
      const rule = monitoringHourRules.data.find((rule: MonitoringHoursRule) => rule.id === ruleId);
      if (rule) {
        setDialogMode('occurrence');
        setSelectedRule({
          id: rule.id,
          eventId: eventId,
          start: start ? localDateToSpecificTimeZoneDate(start, patient.data.timeZone) : DateTime.now().toJSDate(),
          end: end ? localDateToSpecificTimeZoneDate(end, patient.data.timeZone) : DateTime.now().toJSDate(),
          repeats: rule.repeats,
          freq: rule.freq,
          interval: rule.interval,
          byDay: rule.byDay,
          until: rule.until ? DateTime.fromISO(rule.until).toJSDate() : undefined,
          type: rule.type,
          checkInDuration: rule.checkInDuration,
          checkInInterval: rule.checkInInterval,
          updateType: UpdateType.EVENT,
        });
        setOpen(true);
        // setShowFormModal(true);
      }
    } else {
      snackBar.error('Error loading monitoring hours rules!');
    }
  };

  const onRefresh = () => {
    monitoringHoursEvents.refetch();
    monitoringHourRules.refetch();
  };

  const setToday = () => {
    const patientTimeZone = patient.data?.timeZone;
    setCalendarDates({
      start: today.setZone(patientTimeZone || 'local').startOf('week', { useLocaleWeeks: true }),
      end: today.setZone(patientTimeZone || 'local').endOf('week', { useLocaleWeeks: true }),
    });
    const calendarApi = calendarRef.current.getApi();
    calendarApi.today();
  };

  const onSubmit = async (form: MonitoringHoursRuleForm, callback: () => void) => {
    if (form.id) {
      updateRuleOrEvent(form, callback);
    } else {
      createRule(form, callback);
    }
  };

  const onDelete = (form: MonitoringHoursRuleForm, callback: () => void) => {
    const today = DateTime.now().setZone(patient.data!.timeZone).startOf('hour');
    const eventDate = DateTime.fromJSDate(form.start).setZone(patient.data!.timeZone, { keepLocalTime: true });

    if (patient.data && eventDate.diff(today, 'days').days > NUMBER_OF_DAYS_BEHIND) {
      deleteRuleOrEvent(form, callback);
    } else {
      snackBar.error('Cannot delete past monitoring hours!');
    }
  };

  const createRule = (form: MonitoringHoursRuleForm, callback: () => void) => {
    if (patient.data) {
      createMonitoringHourRule.mutate(
        {
          patientId,
          start: dateToUTCString(form.start, patient.data.timeZone),
          end: dateToUTCString(form.end, patient.data.timeZone),
          repeats: form.repeats,
          freq: form.freq,
          interval: form.interval,
          byDay: form.byDay,
          until: form.until ? dateToUTCString(form.until, patient.data.timeZone) : maximumDateInUTCString(),
          type: form.type,
          checkInInterval: form.checkInInterval,
          checkInDuration: form.checkInDuration,
        },
        {
          onSuccess: () => {
            snackBar.success('Monitoring hour rule created successfully!');
            callback();
          },
        },
      );
    } else {
      snackBar.error('Something went wrong. Please try again.');
    }
  };

  const updateRuleOrEvent = async (form: MonitoringHoursRuleForm, callback: () => void) => {
    const mutationOptions: MutationOptions<
      MonitoringHoursEvent | MonitoringHoursRule,
      AxiosError,
      MonitoringHoursEvent | MonitoringHoursRule
    > = {
      onError: (error) => {
        snackBar.error(error.message);
        callback();
      },
      onSuccess: async () => {
        snackBar.success('Monitoring hour rule updated successfully!');
        monitoringHoursEvents.refetch();
        callback();
      },
      onSettled: async () => {
        setSelectedRule(undefined);
      },
    };

    if (patient.data) {
      if (form.updateType === UpdateType.EVENT) {
        updateMonitoringHourEvent.mutate(
          {
            patientId: patientId!,
            id: form.eventId!,
            end: dateToUTCString(form.end, patient.data!.timeZone),
            start: dateToUTCString(form.start, patient.data!.timeZone),
            ruleId: form.id!,
          },
          mutationOptions,
        );
      }
      if (form.updateType === UpdateType.RULE) {
        updateMonitoringHourRule.mutate(
          {
            id: form.id!,
            start: dateToUTCString(form.start, patient.data!.timeZone),
            end: dateToUTCString(form.end, patient.data!.timeZone),
            repeats: form.repeats,
            freq: form.freq,
            interval: form.interval,
            byDay: form.byDay,
            until: form.until ? dateToUTCString(form.until, patient.data!.timeZone) : maximumDateInUTCString(),
            patientId,
            type: form.type,
            checkInInterval: form.checkInInterval,
            checkInDuration: form.checkInDuration,
          },
          mutationOptions,
        );
      }
    } else {
      snackBar.error('Patient not found!');
    }
  };

  const deleteRuleOrEvent = async (form: MonitoringHoursRuleForm, callback: () => void) => {
    const mutationOptions: MutationOptions<void, AxiosError, string | MonitoringHoursRule> = {
      onError: (error) => {
        snackBar.error(error.message);
        callback();
      },
      onSuccess: () => {
        snackBar.success('Monitoring hour rule deleted successfully!');
        monitoringHoursEvents.refetch();
        callback();
      },
      onSettled: () => {
        setSelectedRule(undefined);
      },
    };
    if (
      form.updateType === UpdateType.RULE &&
      patient.data &&
      DateTime.utc()
        .setZone(patient.data.timeZone)
        .startOf('day')
        .diff(DateTime.fromJSDate(form.start).setZone(patient.data.timeZone, { keepLocalTime: true }).toUTC()).days >=
      NUMBER_OF_DAYS_BEHIND
    ) {
      deleteMonitoringHourRule.mutate(
        {
          id: form.id!,
          start: dateToUTCString(form.start, patient.data!.timeZone),
          end: dateToUTCString(form.end, patient.data!.timeZone),
          repeats: form.repeats,
          freq: form.freq,
          interval: form.interval,
          byDay: form.byDay,
          until: form.until ? dateToUTCString(form.until, patient.data!.timeZone) : maximumDateInUTCString(),
          patientId,
          type: form.type,
          checkInInterval: form.checkInInterval,
          checkInDuration: form.checkInDuration,
        },
        mutationOptions,
      );
      return;
    }
    if (form.updateType === UpdateType.EVENT) {
      deleteMonitoringHourEvent.mutate(form.eventId!, mutationOptions);
    }
  };

  if (patient.isLoading) {
    return <Loading />;
  }

  return (
    <Box>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'} spacing={1} sx={{ mb: 1 }}>
        {canCreateMonitoringHours ? (
          <Button variant={'contained'} size={'small'} startIcon={<AddIcon />} onClick={onAddRule}>
            Add rule
          </Button>
        ) : null}
        <Tooltip arrow title={'Refresh monitoring hours'}>
          <IconButton onClick={onRefresh} aria-label={'Refresh monitoring hours'}>
            <RestartAltIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems={'flex-start'}>
        <Box sx={{ width: { xs: '100%', lg: 340 }, flexShrink: 0 }}>
          <MonitoringHoursRulesList
            rules={monitoringHourRules.data ?? []}
            timeZone={patient.data?.timeZone}
            isLoading={monitoringHourRules.isLoading}
            canEdit={canUpdateMonitoringHours}
            onEditRule={onEditRule}
          />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
      {/* FullCalendar has render inconsistencies if we change timeZone after initial render */}
      {patient.data && events && (
        <FullCalendar
          ref={calendarRef}
          plugins={PLUGINS}
          initialView="timeGridWeek"
          height="100%"
          contentHeight="auto"
          slotDuration="01:00:00" // 1 hour
          expandRows={false}
          eventClick={onEventClick}
          editable
          selectable
          customButtons={{
            nextWeek: {
              text: 'Next',
              click: () => {
                onDateChange('nextWeek');
              },
            },
            prevWeek: {
              text: 'Prev',
              click: () => {
                onDateChange('prevWeek');
              },
            },
            today: {
              text: 'Today',
              click: () => {
                setToday();
              },
            },
          }}
          headerToolbar={{
            right: 'today prevWeek,nextWeek',
            left: 'title',
          }}
          events={events}
          timeZone={patient.data.timeZone}
          businessHours={businessHours}
          select={onDateSelect}
          defaultAllDay={false}
          displayEventEnd={true}
          displayEventTime={true}
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short',
          }}
          allDaySlot={false}
          nowIndicator={true}
        />
      )}
        </Box>
      </Stack>
      <MonitoringHoursForm
        open={open}
        mode={dialogMode}
        timeZone={patient.data?.timeZone}
        onClose={() => {
          setOpen(false);
          if (selectedRule) setTimeout(() => setSelectedRule(undefined), theme.transitions.duration.leavingScreen);
        }}
        refetch={onRefresh}
        rule={selectedRule}
        onSubmit={onSubmit}
        onDelete={onDelete}
      />
    </Box>
  );
};

export default MonitoringHours;
